// AM-027 — لایهٔ موقت React Query، مطابق الگوی features/shopfloor/api.ts.
// طبق سابقهٔ AM-016، این فایل باید به‌محض sync‌شدن schema.yml با هوک‌های
// تولیدشدهٔ orval جایگزین شود.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance as apiClient } from "@/api/client"; // ⚠️ مسیر واقعی را با پروژه تطبیق دهید

import type {
  EffectiveShiftResult,
  LeaveRequest,
  OperatorRosterAssignment,
  OvertimeRecord,
  Roster,
  ShiftBoundaryOverride,
  ShiftSwapRequest,
  ShiftTemplate,
} from "./types";

const BASE = "/api/v1/production/shopfloor";

// --- Shift templates ---
export function useShiftTemplates() {
  return useQuery({
    queryKey: ["shift-templates"],
    queryFn: async () => {
      const response = await apiClient.get<any>(`${BASE}/shift-templates/`);
      const data = response.data;
      return (Array.isArray(data) ? data : data?.results) ?? [];
    },
  });
}

export function useCreateShiftTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<ShiftTemplate>) =>
      (await apiClient.post<ShiftTemplate>(`${BASE}/shift-templates/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-templates"] }),
  });
}

// --- Rosters ---
export function useRosters() {
  return useQuery({
    queryKey: ["rosters"],
    queryFn: async () => {
      const res = (await apiClient.get<any>(`${BASE}/rosters/`)).data;
      return res.results || res;
    },
  });
}

export function useCreateRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string; name: string; cycle_length_days: number }) =>
      (await apiClient.post<Roster>(`${BASE}/rosters/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rosters"] }),
  });
}

export function useAddRosterDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { roster: string; day_index: number; shift_template: string }) =>
      (await apiClient.post(`${BASE}/roster-days/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rosters"] }),
  });
}

// --- Operator <-> Roster assignment ---
export function useOperatorRosterAssignments(operatorUserId?: string) {
  return useQuery({
    queryKey: ["operator-roster-assignments", operatorUserId],
    queryFn: async () => {
      const res = (
        await apiClient.get<any>(`${BASE}/operator-roster-assignments/`, {
          params: operatorUserId ? { operator_user_id: operatorUserId } : undefined,
        })
      ).data;
      return res.results || res;
    },
  });
}

export function useAssignOperatorToRoster() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_user_id: string;
      roster: string;
      work_center: string;
      start_date: string;
      end_date?: string | null;
    }) => (await apiClient.post<OperatorRosterAssignment>(`${BASE}/operator-roster-assignments/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-roster-assignments"] }),
  });
}

export function useResolveEffectiveShift(operatorUserId: string, date: string, enabled = true) {
  return useQuery({
    queryKey: ["resolve-effective-shift", operatorUserId, date],
    enabled: enabled && !!operatorUserId && !!date,
    queryFn: async () =>
      (
        await apiClient.get<EffectiveShiftResult>(`${BASE}/operator-roster-assignments/resolve/`, {
          params: { operator_user_id: operatorUserId, date },
        })
      ).data,
  });
}

// --- Leave ---
export function useLeaveTypes() {
  return useQuery({
    queryKey: ["leave-types"],
    queryFn: async () => {
      const res = (await apiClient.get<any>(`${BASE}/leave-types/`)).data;
      return res.results || res;
    },
  });
}

export function useCreateLeaveType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string; name: string; is_paid: boolean; allow_hourly: boolean }) =>
      (await apiClient.post(`${BASE}/leave-types/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-types"] }),
  });
}

export function useUpdateLeaveType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; code: string; name: string; is_paid: boolean; allow_hourly: boolean }) =>
      (await apiClient.put(`${BASE}/leave-types/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-types"] }),
  });
}

export function useDeleteLeaveType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(`${BASE}/leave-types/${id}/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-types"] }),
  });
}

export function useLeaveRequests(operatorUserId?: string) {
  return useQuery({
    queryKey: ["leave-requests", operatorUserId],
    queryFn: async () => {
      const res = (
        await apiClient.get<any>(`${BASE}/leave-requests/`, {
          params: operatorUserId ? { operator_user_id: operatorUserId } : undefined,
        })
      ).data;
      return res.results || res;
    },
  });
}

export function useSubmitLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_user_id: string;
      leave_type: string;
      start_datetime: string;
      end_datetime: string;
      reason?: string;
    }) => (await apiClient.post<LeaveRequest>(`${BASE}/leave-requests/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useApproveLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post<LeaveRequest>(`${BASE}/leave-requests/${id}/approve/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

export function useRejectLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<LeaveRequest>(`${BASE}/leave-requests/${id}/reject/`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leave-requests"] }),
  });
}

// --- Shift swap ---
export function useShiftSwapRequests() {
  return useQuery({
    queryKey: ["shift-swap-requests"],
    queryFn: async () => {
      const res = (await apiClient.get<any>(`${BASE}/shift-swap-requests/`)).data;
      return res.results || res;
    },
  });
}

export function useRequestShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_a_user_id: string;
      date_a: string;
      operator_b_user_id: string;
      date_b: string;
      reason?: string;
    }) => (await apiClient.post<ShiftSwapRequest>(`${BASE}/shift-swap-requests/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-swap-requests"] }),
  });
}

export function useApproveShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post<ShiftSwapRequest>(`${BASE}/shift-swap-requests/${id}/approve/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-swap-requests"] }),
  });
}

export function useRejectShiftSwap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.post<ShiftSwapRequest>(`${BASE}/shift-swap-requests/${id}/reject/`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-swap-requests"] }),
  });
}

// --- Overtime ---
export function useOvertimeRecords() {
  return useQuery({
    queryKey: ["overtime-records"],
    queryFn: async () => {
      const res = (await apiClient.get<any>(`${BASE}/overtime-records/`)).data;
      return res.results || res;
    },
  });
}

export function usePlanOvertime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      operator_user_id: string;
      work_center: string;
      date: string;
      position: "before" | "after";
      planned_start: string;
      planned_end: string;
      reason?: string;
    }) => (await apiClient.post<OvertimeRecord>(`${BASE}/overtime-records/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["overtime-records"] }),
  });
}

// --- Shift boundary override (crew-wide) ---
export function useShiftBoundaryOverrides() {
  return useQuery({
    queryKey: ["shift-boundary-overrides"],
    queryFn: async () => {
      const res = (await apiClient.get<any>(`${BASE}/shift-boundary-overrides/`)).data;
      return res.results || res;
    },
  });
}

export function useCreateShiftBoundaryOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      work_center: string;
      date: string;
      shift_template: string;
      effective_start_time: string;
      effective_end_time: string;
      reason?: string;
    }) => (await apiClient.post<ShiftBoundaryOverride>(`${BASE}/shift-boundary-overrides/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-boundary-overrides"] }),
  });
}
